import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Card, Container, Row, Col, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../../context/appContext";
import { accountForLogin } from "../../utils/env_config";
import { Auth0Error, INVALID_CREDENTIALS, authenticate, clearAuthSession } from "../../utils/auth0";
import { clearLoginMessage, peekLoginMessage } from "../../utils/session";
import { Loader } from "../../components";

const DEFAULT_ENV = "dev";

const SIGN_IN_FAILED = "Could not sign in. Please try again.";

// The credentials were accepted and the failure came afterwards, loading the workspace.
const WORKSPACE_LOAD_FAILED = "Signed in, but the workspace could not be loaded. Please try again.";

const LogInPage = ({ isLoading, handleEnvChange }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(peekLoginMessage);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { loginUser, logoutUser } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    clearLoginMessage();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    // Reject anything that is not one of the two known accounts before contacting the
    // identity provider.
    const account = accountForLogin(username);
    if (!account) {
      setError(INVALID_CREDENTIALS);
      return;
    }

    setLoading(true);

    try {
      await authenticate({ email: account.email, password, env: DEFAULT_ENV });
    } catch (err) {
      clearAuthSession();
      setError(err instanceof Auth0Error ? err.message : SIGN_IN_FAILED);
      setLoading(false);
      return;
    }

    try {
      loginUser({ email: account.email, username: account.username, role: account.role });
      await handleEnvChange(DEFAULT_ENV);
      navigate("/tenants");
    } catch (err) {
      logoutUser();
      setError(WORKSPACE_LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (isLoading) {
    return null;
  }

  return (
    <Container>
      <Row className="justify-content-md-center mt-5">
        <Col xs={12} md={6}>
          <Card className="login-card mb-5">
            <Card.Body>
              <Card.Title className="text-center mb-4">Log In</Card.Title>
              <Form onSubmit={handleLogin}>
                <Form.Group className="mb-3" controlId="formUsername">
                  <Form.Label>Username/Email</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter username or email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>Password</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <InputGroup.Text onClick={togglePasswordVisibility} style={{ cursor: "pointer" }}>
                      <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                    </InputGroup.Text>
                  </InputGroup>
                </Form.Group>

                {error && <p className="text-danger">{error}</p>}

                <div className="d-grid">
                  {loading ? (
                    <Loader />
                  ) : (
                    <Button variant="primary" type="submit" className="mx-auto login-button" disabled={isLoading}>
                      Login
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LogInPage;
